import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import familyService, { FamilyData, FamilyAnalysisResponse } from '@/services/familyService';
import ToastService from '@/utils/toast';

export const useFamilyData = () => {
  const queryClient = useQueryClient();
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Query for family data
  const {
    data: familyData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['family-data'],
    queryFn: familyService.getFamilyData,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Query for missing information
  const {
    data: missingInfo,
    refetch: refetchMissingInfo
  } = useQuery({
    queryKey: ['family-missing-info'],
    queryFn: familyService.getMissingInfo,
    enabled: !!familyData,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Mutation for updating family data
  const updateFamilyMutation = useMutation({
    mutationFn: familyService.updateFamilyData,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family-data'] });
      queryClient.invalidateQueries({ queryKey: ['family-missing-info'] });
      ToastService.showSuccess('Family information updated successfully!');
    },
    onError: (error: any) => {
      console.error('Failed to update family data:', error);
      ToastService.showError('Failed to update family information. Please try again.');
    },
  });

  // Analyze family description
  const analyzeFamilyDescription = useCallback(async (description: string): Promise<FamilyAnalysisResponse | null> => {
    if (!description.trim()) {
      ToastService.showError('Please enter a family description');
      return null;
    }

    setIsAnalyzing(true);
    try {
      const result = await familyService.analyzeFamilyDescription(description);
      
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['family-data'] });
      queryClient.invalidateQueries({ queryKey: ['family-missing-info'] });
      
      ToastService.showSuccess('Family information analyzed successfully!');
      return result;
    } catch (error: any) {
      console.error('Failed to analyze family description:', error);
      ToastService.showError('Failed to analyze family description. Please try again.');
      return null;
    } finally {
      setIsAnalyzing(false);
    }
  }, [queryClient]);

  // Complete family data with additional information
  const completeFamilyData = useCallback(async (additionalInfo: string): Promise<FamilyAnalysisResponse | null> => {
    if (!additionalInfo.trim()) {
      ToastService.showError('Please enter additional information');
      return null;
    }

    try {
      const result = await familyService.completeFamilyData(additionalInfo);
      
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['family-data'] });
      queryClient.invalidateQueries({ queryKey: ['family-missing-info'] });
      
      ToastService.showSuccess('Family information updated successfully!');
      return result;
    } catch (error: any) {
      console.error('Failed to complete family data:', error);
      ToastService.showError('Failed to update family information. Please try again.');
      return null;
    }
  }, [queryClient]);

  // Update family data
  const updateFamilyData = useCallback((data: FamilyData) => {
    updateFamilyMutation.mutate(data);
  }, [updateFamilyMutation]);

  return {
    // Data
    familyData: familyData || {},
    missingInfo: missingInfo || [],
    
    // Loading states
    isLoading,
    isAnalyzing,
    isUpdating: updateFamilyMutation.isPending,
    
    // Error states
    error,
    
    // Actions
    analyzeFamilyDescription,
    completeFamilyData,
    updateFamilyData,
    refetch,
    refetchMissingInfo,
  };
};